<template>
  <div v-if="cmsData && content">
    <StoryblokComponent
      :id="appStore.slug"
      v-editable="cmsData"
      :blok="content"
    />

    <GeneralElementsBackToTop v-if="content.showBackToTop ?? false" />
  </div>
</template>

<script setup lang="ts">
  import { useAppStore } from '~/stores'
  import StoryBlok from '~/services/Storyblok'
  import { useFetchStoryData } from '~/composables/useFetchStoryData'
  import formatSbPath from '~/utils/formatSbPath'
  import { useSEOPaths } from '~/utils/composables/seoHeaderPaths'
  import formatUtil from '~/utils/formatUtil'
  import { getVacancyRedirectPath } from '~/utils/vacancyRoutes'

  type CmsData = Awaited<ReturnType<typeof StoryBlok.pull>>

  const { locale, locales } = useI18n()
  const route = useRoute()
  const appStore = useAppStore()
  const { fetchStoryPath } = useFetchStoryData()

  const getErrorStatusCode = (error: unknown): number | undefined => {
    if (!error || typeof error !== 'object') {
      return undefined
    }

    if ('statusCode' in error && typeof error.statusCode === 'number') {
      return error.statusCode
    }

    if ('status' in error && typeof error.status === 'number') {
      return error.status
    }

    if (
      'response' in error &&
      error.response &&
      typeof error.response === 'object' &&
      'status' in error.response &&
      typeof error.response.status === 'number'
    ) {
      return error.response.status
    }

    return undefined
  }

  const cmsPath = route.path === `/${locale.value}` ? '' : route.path.replace(`/${locale.value}/`, '')

  const storyblokPath = cmsPath.replace(/^careers\//, '')

  const loadCmsData = async (): Promise<CmsData | null> => {
    try {
      return await StoryBlok.pull(storyblokPath)
    } catch (error: unknown) {
      const statusCode = getErrorStatusCode(error)
      const redirectPath = getVacancyRedirectPath(route.path)

      if (statusCode !== 404 || !redirectPath) {
        throw error
      }

      if (import.meta.server) {
        const event = useRequestEvent()

        if (!event) {
          throw error
        }

        await sendRedirect(event, redirectPath, 301)

        return null
      }

      await navigateTo(redirectPath, {
        replace: true
      })

      return null
    }
  }

  const cmsData = await loadCmsData()
  const content = cmsData?.content ?? null

  if (cmsData && content) {
    const imageBlok = {
      image: content.heroImage
    }

    appStore.slug = cmsData.slug

    if (content.subscribeTag !== '' && content.subscribeTag !== undefined) {
      appStore.subscribeTag = content.subscribeTag
    }

    appStore.hideNavigation = content.hideNavigation ?? false
    appStore.disableCVBlock = content.disableCVBlock ?? false
    appStore.newsletterTitle = content.newsletterTitle ?? ''
    appStore.newsletterDescription = content.newsletterDescription ?? ''
    appStore.newsletterDisclaimer = renderRichText(content.newsletterDisclaimer) ?? ''

    // Handle show date
    const now = formatUtil(new Date(), 'yyyy-MM-dd')
    const validPagesWithStartDateInPast = ['vacancy-page-ta']

    if (!validPagesWithStartDateInPast.includes(content.component) && content.startDate && content.startDate < now) {
      throw showError({
        statusCode: 404,
        fatal: true,
        message: 'Page not found'
      })
    }

    // Handle translated paths
    const tPaths: Record<string, string> = {}

    const handleDefaultUrls = (localeItem: any) => {
      if (localeItem.code === 'en') {
        tPaths[localeItem.code] = `/${localeItem.code}${formatSbPath(cmsData.default_full_slug)}`
      }

      const translatedSlug = cmsData.translated_slugs.find((slug: any) => slug.lang === localeItem.code)

      if (translatedSlug) {
        tPaths[localeItem.code] = `/${localeItem.code}${formatSbPath(translatedSlug.path)}`
      }
    }

    if (content.showInLanguages) {
      locales.value.forEach((localeItem: any) => {
        const code = localeItem.code === 'en' ? 'default' : localeItem.code

        const allowed = content.showInLanguages.find((lang: string) => lang === code)

        if (allowed) {
          handleDefaultUrls(localeItem)
        } else if (content.redirectUrl) {
          fetchStoryPath(localeItem.code, content.redirectUrl.id).then((path) => {
            const formattedPath = formatSbPath(path, localeItem.code)

            tPaths[localeItem.code] = `/${localeItem.code}${formattedPath}`

            appStore.slugs = {
              ...appStore.slugs,
              [localeItem.code]: tPaths[localeItem.code]
            }
          })
        } else {
          tPaths[localeItem.code] = `/${localeItem.code}`
        }
      })
    } else {
      locales.value.forEach((localeItem: any) => {
        handleDefaultUrls(localeItem)
      })
    }

    appStore.slugs = tPaths

    const defaultShareImage = 'https://a.storyblok.com/f/236210/1910x1000/b4c11be989/share-vacancy.png/m/1200x630'

    const heroShareImage = imageBlok.image?.filename ? `${imageBlok.image.filename}/m/1200x630` : ''

    const getShareImage = (type: string) => {
      if (content.meta?.[`${type}_image`]) {
        return `${content.meta[`${type}_image`]}/m/1200x630`
      }

      return heroShareImage || defaultShareImage
    }

    useSeoMeta({
      title: () => content.meta?.title || 'airBaltic People',
      description: () => content.meta?.description,
      ogUrl: () => route.path,
      ogTitle: () => content.meta?.og_title || content.meta?.title,
      ogDescription: () => content.meta?.og_description || content.meta?.description,
      ogType: 'website',
      ogImage: () => getShareImage('og'),
      ogSiteName: 'careers @ airBaltic',
      twitterTitle: () => content.meta?.twitter_title || content.meta?.title,
      twitterDescription: () => content.meta?.twitter_description || content.meta?.description,
      twitterImage: () => getShareImage('twitter'),
      twitterCard: 'summary'
    })

    useSEOPaths()
  }
</script>
